import type { ArmyList } from '../types/army';
import type { Faction } from '../types/faction';
import type { ValidationIssue } from './armyValidation';

/**
 * Core rule (Warhammer: The Old World rulebook, Magic Items): an army may not
 * include more than one of any particular magic item.
 *
 * Budgeted upgrade families (Gifts of Chaos, Chaos Mutations, Forest Spites,
 * Vampiric Powers, Chaotic Traits) enforce their own once-per-army limits inside
 * validateArmy. This covers ordinary magic items - personal items and magic
 * standards - selected across multiple characters, which previously went
 * unchecked.
 *
 * Counts both entry.selectedMagicItemIds and the separately-tracked BSB magic
 * standard (selectedBsbStandardId). Any id appearing more than once army-wide is
 * a duplicate, except items marked extremely common - the rulebook (p.338) puts
 * no limit on how many of those an army may include.
 */
export function findDuplicateMagicItems(army: ArmyList, faction: Faction): ValidationIssue[] {
  const counts = new Map<string, number>();
  for (const entry of army.entries) {
    const ids = [...(entry.selectedMagicItemIds ?? [])];
    if (entry.selectedBsbStandardId) ids.push(entry.selectedBsbStandardId);
    for (const id of ids) {
      counts.set(id, (counts.get(id) ?? 0) + 1);
    }
  }

  const issues: ValidationIssue[] = [];
  for (const [id, count] of counts) {
    if (count <= 1) continue;
    const item = faction.magic_items.find((i) => i.id === id);
    if (!item) continue;
    if ((item as { extremely_common?: boolean }).extremely_common) continue;
    issues.push({
      category: 'Magic items',
      message: `${item.name} may only be taken once per army (taken ${count} times)`,
      severity: 'error',
    });
  }
  return issues;
}
